// Centrale BRAND_CONFIG (frontend) — multibrand/white-label domeinrouting.
//
// Identieke data als backend/src/config/brands.js — houd beide in sync. De data-
// scope wordt ALTIJD server-side per energy_label afgedwongen; het hostname
// bepaalt enkel branding + naar welk domein een gebruiker hoort (UX), nooit
// autorisatie. Een ander domein openen geeft dus nooit data van een ander label.

// VASTE NAAMCONVENTIE: per merk staat hier één `domain`; de hostnamen volgen daar
// automatisch uit als `partners.<domain>` (partnerportaal) en `portal.<domain>`
// (klantenportaal). Een NIEUW energielabel toevoegen = één regel met brand_key +
// energy_label + display_name + domain; hostnamen nooit handmatig invullen.
// `energy_label` moet EXACT gelijk zijn aan order.company uit de ET-data.
const BRANDS = [
  { brand_key: 'energytogether', energy_label: null,              isMain: true,  display_name: 'Energy Together', domain: 'energytogether.be' },
  { brand_key: 'evident',        energy_label: 'Evident Energie', isMain: false, display_name: 'Evident Energie',  domain: 'evidentenergie.be' },
  { brand_key: 'power2you',      energy_label: 'Power2You',       isMain: false, display_name: 'Power2You',        domain: 'power2you.be' },
  { brand_key: 'servolt',        energy_label: 'Servolt Energie', isMain: false, display_name: 'Servolt Energie',  domain: 'servoltenergie.be' },
  { brand_key: 'smappee',        energy_label: 'Smappee Smiles',  isMain: false, display_name: 'Smappee Smiles',   domain: 'smappeesmiles.be' },
  { brand_key: 'hoa',            energy_label: 'HOA Energy',      isMain: false, display_name: 'HOA Energy',       domain: 'hoa.energy' },
  // Label heet in de ET-data voluit "Prijspunten Energie België" (= order.company);
  // het domein is prijspuntenenergie.be (prijspunten.be bestaat niet in DNS).
  { brand_key: 'prijspunten',    energy_label: 'Prijspunten Energie België', isMain: false, display_name: 'Prijspunten Energie', domain: 'prijspuntenenergie.be' },
];

export const BRAND_CONFIG = BRANDS.map((b) => ({
  ...b,
  partner_host: `partners.${b.domain}`,
  portal_host: `portal.${b.domain}`,
}));

export const MAIN_BRAND = BRAND_CONFIG.find((b) => b.isMain);

const normHost = (hostname) => String(hostname || '').toLowerCase().trim().split(':')[0];

export function isDevHost(hostname) {
  const h = normHost(hostname);
  return !h || h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local') || h.endsWith('.onrender.com');
}

// De technische Render-URL (*.onrender.com) — hiervandaan sturen we admin/ET naar
// het canonieke hoofdmerk-domein; localhost (ook dev) NIET.
export function isOnrenderHost(hostname) {
  return normHost(hostname).endsWith('.onrender.com');
}

// → { brand, dev:false } bij bekend domein; { brand:null, dev:true } bij dev/onrender;
//   { brand:undefined, dev:false } bij onbekend domein.
export function getBrandFromHostname(hostname) {
  if (isDevHost(hostname)) return { brand: null, dev: true };
  const h = normHost(hostname);
  const brand = BRAND_CONFIG.find((b) => h === b.partner_host || h === b.portal_host);
  return { brand: brand || undefined, dev: false };
}

export function brandForUser(user) {
  if (!user) return null;
  if (user.role === 'admin' || user.role === 'et_dossier') return MAIN_BRAND;
  const label = user.energyLabel || user.energy_label || null;
  if (!label) return MAIN_BRAND;
  return BRAND_CONFIG.find((b) => b.energy_label === label) || MAIN_BRAND;
}

export function getExpectedDomainForUser(user, portalType = 'partner') {
  const b = brandForUser(user);
  if (!b) return null;
  return portalType === 'portal' ? b.portal_host : b.partner_host;
}

// action: 'allow' | 'redirect' | 'unknown'  (+ brand/expected/expectedHost/dev)
export function evaluateBrandAccess(user, hostname, portalType = 'partner') {
  const { brand, dev } = getBrandFromHostname(hostname);
  if (dev) return { action: 'allow', dev: true };
  if (brand === undefined) return { action: 'unknown' };
  const expected = brandForUser(user);
  if (expected && brand.brand_key === expected.brand_key) return { action: 'allow', brand };
  return { action: 'redirect', brand, expected, expectedHost: portalType === 'portal' ? expected.portal_host : expected.partner_host };
}

// Mag het PLATFORMLOGO (`/logo.png`) op dit domein getoond worden?
//
// Alleen op het hoofdmerk-domein — en in dev, waar geen merk aan het hostname
// hangt. Op een merkdomein toont een scherm dat zijn huisstijl nog niet binnen
// heeft liever niets dan één tekening lang het verkeerde merk; dat is dezelfde
// compliance-regel als in de app zelf (zie CLAUDE.md: nooit terugvallen op het
// ET-logo bij een partnerrol). Een onbekend domein telt niet als hoofddomein.
export function etLogoToegestaan(hostname) {
  const { brand, dev } = getBrandFromHostname(hostname);
  return dev || !!(brand && brand.isMain);
}
